import React, {
  forwardRef,
  ComponentPropsWithRef,
  ElementType,
  ForwardRefExoticComponent,
  JSX,
  ReactElement,
} from 'react';
import classNames from 'classnames';

import 'app/assets/stylesheets/design_system/body.scss';

export type BaseBodyProps = {
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  bold?: boolean;
  className?: string;
};

type Merge<P1 = object, P2 = object> = Omit<P1, keyof P2> & P2;
type MergeProps<E, P = object> = P &
  Merge<E extends ElementType ? ComponentPropsWithRef<E> : never, P>;

interface ForwardRefComponent<IntrinsicElementString, OwnProps = object>
  extends ForwardRefExoticComponent<
    MergeProps<
      IntrinsicElementString,
      OwnProps & { as?: IntrinsicElementString }
    >
  > {
  <As extends keyof JSX.IntrinsicElements>(
    props: MergeProps<As, OwnProps & { as: As }>,
  ): ReactElement | null;

  <
    As extends ElementType<unknown>,
    _AsWithProps = As extends ElementType<infer P> ? ElementType<P> : never,
  >(
    props: MergeProps<_AsWithProps, OwnProps & { as: _AsWithProps }>,
  ): ReactElement | null;
}

/**
 * Polymorphic component for body-styled text.
 * If you need a link, instead refer to BodyLink component.
 *
 * Props:
 * - `size`: Controls the font-size and line-height of the text (default: 'md')
 *           Choose between 'xs' | 'sm' | 'md' | 'lg' | 'xl'
 * - `bold`: Increases the font weight. (default: false)
 * - `as`:   The element type to render as. Defaults to 'p'.
 *           Never use 'a', instead use BodyLink component.
 *
 * ## Basic Usage
 *
 * @example
 *
 * ```tsx
 * import { Body } from 'js/design_system/Body';
 *
 * <Body>A simple paragraph</Body>
 * ```
 * ---
 *
 * ## Custom Size
 *
 * @example
 *
 * ```tsx
 * import { Body } from 'js/design_system/Body';
 *
 * <Body size='lg'>Bigger body text</Body>
 * ```
 * ---
 *
 * ## Bold
 * ```tsx
 * import { Body } from 'js/design_system/Body';
 *
 * <Body bold>Bolded body text</Body>
 * ```
 * ---
 *
 */
export const Body = forwardRef(
  (
    {
      as = 'p',
      size = 'md',
      bold = false,
      className,
      children,
      ...restProps
    }: React.PropsWithChildren<BaseBodyProps> & {
      as: ElementType;
    },
    ref,
  ) => {
    const Tag = as ?? 'p';

    const classes = classNames(
      className,
      `ds2-5-body-${size}${bold ? '-bold' : ''}`,
      {
        'ds2-5-body-link': as === 'button',
      },
    );

    return (
      <Tag className={classes} {...restProps} ref={ref}>
        {children}
      </Tag>
    );
  },
) as ForwardRefComponent<ElementType, React.PropsWithChildren<BaseBodyProps>>;
